import { forwardRef, useRef, useState, useCallback, useEffect, type CSSProperties } from 'react';
import { FaPencilAlt } from 'react-icons/fa';
import { MdDelete } from 'react-icons/md';
import { HiX } from 'react-icons/hi';
import { HiChevronLeft, HiChevronRight } from 'react-icons/hi';
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import {
  SortableContext,
  verticalListSortingStrategy,
  useSortable,
  arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import { Modal, ModalMethods, Text, Button, toasts, Loader } from '../../../../../ui-component';
import { api } from '../../../../../utils/apiRequest';
import { getValueFromLocalStorage } from '../../../../../utils/common';
import AddMeasurementModal from './AddMeasurementModal';
import EditMeasurementNameModal from './EditMeasurementNameModal';
import MeasurementIconSelectorModal, { MeasurementIconType } from './MeasurementIconSelectorModal';

type MeasurementParam = {
  id: number;
  name: string;
  icon_url?: string;
};

type BoutiqueMeasurementsResponse = {
  id: number;
  params: MeasurementParam[];
  atomic_outfit_response?: {
    id: number;
    name: string;
    gender?: string;
  };
  boutique_id: number;
  measurement_scale?: {
    scale: string;
  };
};

type OutfitPiece = {
  id: number;
  name: string;
  outfit_link?: string;
};

type MeasurementsModalProps = {
  outfitName: string;
  outfitPieces: OutfitPiece[];
  selectedPieceId: number | null;
  onPieceChange?: (pieceId: number) => void;
  onMeasurementsUpdated?: () => void;
};

type SortableMeasurementItemProps = {
  param: MeasurementParam;
  onEditName: (param: MeasurementParam) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onEditIcon: (param: MeasurementParam, e: any) => void;
  onDelete: (id: number) => void;
  disabled?: boolean;
};

const SortableMeasurementItem = ({
  param,
  onEditName,
  onEditIcon,
  onDelete,
  disabled,
}: SortableMeasurementItemProps) => {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: param.id,
    disabled,
  });

  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    touchAction: 'none',
  };

  const baseClass =
    'flex items-center gap-3 p-3 rounded-lg border transition-all duration-200 select-none';
  const stateClass = isDragging
    ? 'opacity-80 bg-gray-50 border-gray-200 hover:bg-gray-100'
    : 'bg-gray-50 border-gray-200 hover:bg-gray-100';
  const cursorClass = disabled ? 'cursor-not-allowed' : 'cursor-grab active:cursor-grabbing';

  return (
    <div
      ref={setNodeRef}
      style={style}
      {...attributes}
      {...listeners}
      className={`${baseClass} ${stateClass} ${cursorClass}`}
    >
      <div
        className="w-12 h-12 rounded-lg bg-blue-100 flex items-center justify-center flex-shrink-0 overflow-hidden cursor-pointer hover:bg-blue-200 transition-colors relative group"
        onClick={(e) => onEditIcon(param, e)}
        onMouseDown={(e) => e.stopPropagation()}
        title="Click to update icon"
      >
        {param.icon_url ? (
          <img
            src={param.icon_url}
            alt={param.name}
            className="w-full h-full object-cover rounded-lg"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center bg-gray-200 rounded-lg">
            <span className="text-gray-400 text-xs">+</span>
          </div>
        )}
        <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-10 rounded-lg transition-all flex items-center justify-center">
          <Text
            size="small"
            color="white"
            className="opacity-0 group-hover:opacity-100 transition-opacity font-inter"
          >
            Change
          </Text>
        </div>
      </div>
      <div className="flex-1 min-w-0">
        <Text className="font-inter text-sm truncate" color="black">
          {param.name}
        </Text>
      </div>
      <button
        type="button"
        onClick={() => onEditName(param)}
        onMouseDown={(e) => e.stopPropagation()}
        className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors flex-shrink-0"
        aria-label="Edit measurement name"
        disabled={disabled}
      >
        <FaPencilAlt size={14} />
      </button>
      <button
        type="button"
        onClick={() => onDelete(param.id)}
        onMouseDown={(e) => e.stopPropagation()}
        className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors flex-shrink-0"
        aria-label="Delete measurement"
        disabled={disabled}
      >
        <MdDelete size={18} />
      </button>
    </div>
  );
};

const MeasurementsModal = forwardRef<ModalMethods, MeasurementsModalProps>(
  ({ outfitName, outfitPieces, selectedPieceId, onPieceChange, onMeasurementsUpdated }, ref) => {
    const addMeasurementModalRef = useRef<ModalMethods>(null);
    const editNameModalRef = useRef<ModalMethods>(null);
    const iconSelectorModalRef = useRef<ModalMethods>(null);

    const [measurementsData, setMeasurementsData] = useState<BoutiqueMeasurementsResponse | null>(
      null
    );
    const [isLoading, setIsLoading] = useState(false);
    const [selectedParamForEdit, setSelectedParamForEdit] = useState<MeasurementParam | null>(null);
    const [selectedParamForIcon, setSelectedParamForIcon] = useState<MeasurementParam | null>(null);
    const [isReordering, setIsReordering] = useState(false);

    const sensors = useSensors(
      useSensor(PointerSensor, {
        activationConstraint: {
          distance: 8,
        },
      })
    );

    const currentPiece = outfitPieces.find((p) => p.id === selectedPieceId);

    const fetchMeasurements = useCallback(async () => {
      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id || !selectedPieceId) {
        setMeasurementsData(null);

        return;
      }

      setIsLoading(true);

      try {
        const response = await api.getRequest(
          `new_measurement/v2/${boutique_id}/boutique_measurements?atomic_outfit_id=${selectedPieceId}`
        );

        if (response.status && response.data) {
          setMeasurementsData(response.data);
        } else {
          setMeasurementsData(null);
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'fetch-measurements-error');
        }
        setMeasurementsData(null);
      } finally {
        setIsLoading(false);
      }
    }, [selectedPieceId]);

    useEffect(() => {
      void fetchMeasurements();
    }, [fetchMeasurements]);

    const handleEditName = (param: MeasurementParam) => {
      setSelectedParamForEdit(param);
      editNameModalRef.current?.show();
    };

    const handleEditIcon = (param: MeasurementParam, e: React.MouseEvent) => {
      e.stopPropagation();
      setSelectedParamForIcon(param);
      iconSelectorModalRef.current?.show();
    };

    const handleIconSelect = async (icon: MeasurementIconType) => {
      if (!selectedParamForIcon || !measurementsData) {
        return;
      }

      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      try {
        const response = await api.putRequest(
          `new_measurement/v2/${boutique_id}/boutique_measurements/${selectedParamForIcon.id}`,
          {
            id: selectedParamForIcon.id,
            image_link: icon.image_link || icon.reference_id,
          }
        );

        if (response.status) {
          toasts(
            'success',
            'Measurement icon updated successfully',
            'measurement-icon-update-success'
          );

          void fetchMeasurements();

          if (onMeasurementsUpdated) {
            onMeasurementsUpdated();
          }
        } else {
          toasts(
            'error',
            response.message || 'Failed to update measurement icon',
            'measurement-icon-update-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'measurement-icon-update-error');
        }
      } finally {
        setSelectedParamForIcon(null);
      }
    };

    const handleDelete = async (paramId: number) => {
      if (!measurementsData) {
        return;
      }

      const boutique_id = getValueFromLocalStorage('boutique_id');

      if (!boutique_id) {
        toasts('error', 'Boutique ID not found', 'boutique-id-missing');

        return;
      }

      try {
        const response = await api.deleteRequest(
          `new_measurement/v2/${boutique_id}/boutique_measurements/${paramId}`
        );

        if (response.status) {
          toasts('success', 'Measurement removed successfully', 'measurement-delete-success');

          void fetchMeasurements();

          if (onMeasurementsUpdated) {
            onMeasurementsUpdated();
          }
        } else {
          toasts(
            'error',
            response.message || 'Failed to remove measurement',
            'measurement-delete-error'
          );
        }
      } catch (err) {
        if (err instanceof Error) {
          toasts('error', err.message, 'measurement-delete-error');
        }
      }
    };

    const handleNavigatePiece = (direction: 'prev' | 'next') => {
      const currentIndex = outfitPieces.findIndex((p) => p.id === selectedPieceId);

      if (currentIndex === -1) {
        return;
      }

      const newIndex = direction === 'prev' ? currentIndex - 1 : currentIndex + 1;

      if (newIndex >= 0 && newIndex < outfitPieces.length) {
        const newPiece = outfitPieces[newIndex];

        if (onPieceChange) {
          onPieceChange(newPiece.id);
        }
      }
    };

    const headerComponent = (
      <div className="flex items-center justify-between w-full pr-8">
        <Text size="large" fontWeight={700} className="font-inter">
          Measurements - {outfitName}
        </Text>
        <button
          type="button"
          onClick={() => {
            if (typeof ref === 'object' && ref !== null && 'current' in ref && ref.current) {
              ref.current.hide();
            }
          }}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
        >
          <Text size="small" color="black" className="font-inter">
            Close
          </Text>
          <HiX size={20} />
        </button>
      </div>
    );

    const params = measurementsData?.params ?? [];
    const existingParamIds = params.map((p) => p.id);

    const persistMeasurementsOrder = useCallback(
      async (orderedParams: MeasurementParam[]) => {
        const boutique_id = getValueFromLocalStorage('boutique_id');

        if (!boutique_id || orderedParams.length === 0) {
          return;
        }

        setIsReordering(true);

        try {
          const response = await api.putRequest(
            `new_measurement/${boutique_id}/boutique_measurements/swap`,
            orderedParams.map((p) => p.id)
          );

          if (response.status) {
            toasts('success', 'Order updated', 'measurements-order-saved');

            if (onMeasurementsUpdated) {
              onMeasurementsUpdated();
            }
          } else {
            toasts(
              'error',
              response.message || 'Failed to update order',
              'measurements-order-failed'
            );
            void fetchMeasurements();
          }
        } catch (err) {
          if (err instanceof Error) {
            toasts('error', err.message, 'measurements-order-failed');
          }
          void fetchMeasurements();
        } finally {
          setIsReordering(false);
        }
      },
      [fetchMeasurements, onMeasurementsUpdated]
    );

    const handleDragEnd = useCallback(
      (event: DragEndEvent) => {
        const { active, over } = event;

        if (!over || active.id === over.id) {
          return;
        }

        setMeasurementsData((prev) => {
          if (!prev) {
            return prev;
          }

          const oldIndex = prev.params.findIndex((item) => item.id === active.id);
          const newIndex = prev.params.findIndex((item) => item.id === over.id);

          if (oldIndex === -1 || newIndex === -1) {
            return prev;
          }

          const newParams = arrayMove(prev.params, oldIndex, newIndex);

          void persistMeasurementsOrder(newParams);

          return { ...prev, params: newParams };
        });
      },
      [persistMeasurementsOrder]
    );

    const measurementIds = params.map((p) => p.id);

    const isDndDisabled = isLoading || isReordering || params.length === 0;

    return (
      <>
        <Modal
          ref={ref}
          size="xl"
          headerComponent={headerComponent}
          showFooter={false}
          showCloseIcon={false}
        >
          <div className="flex flex-col h-full min-h-[400px]">
            <div className="mb-4">
              <Text className="font-inter text-sm text-gray-600 mb-4 block mt-1" color="black">
                For each piece, pick measurements from Darzee&apos;s library (with reference images)
                or add custom fields with your own images.
              </Text>

              {outfitPieces.length > 0 && (
                <div className="flex items-center gap-2 mb-4">
                  <button
                    type="button"
                    onClick={() => handleNavigatePiece('prev')}
                    disabled={outfitPieces.findIndex((p) => p.id === selectedPieceId) <= 0}
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <HiChevronLeft size={18} />
                  </button>
                  <button
                    type="button"
                    className="flex items-center gap-2 px-4 py-2 rounded-full bg-gray-100 hover:bg-gray-200 min-w-[200px]"
                  >
                    {currentPiece?.outfit_link ? (
                      <img
                        src={currentPiece.outfit_link}
                        alt={currentPiece.name}
                        className="w-8 h-8 rounded-lg object-cover"
                      />
                    ) : (
                      <span className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center text-gray-500 text-xs">
                        +
                      </span>
                    )}
                    <Text className="font-inter text-sm truncate" color="black">
                      {currentPiece?.name ?? 'Select piece'}
                    </Text>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleNavigatePiece('next')}
                    disabled={
                      outfitPieces.findIndex((p) => p.id === selectedPieceId) >=
                      outfitPieces.length - 1
                    }
                    className="w-8 h-8 rounded-full bg-gray-200 flex items-center justify-center hover:bg-gray-300 disabled:opacity-40 disabled:cursor-not-allowed"
                  >
                    <HiChevronRight size={18} />
                  </button>
                </div>
              )}

              {params.length > 0 && (
                <Text className="font-inter text-xs text-gray-500 mb-2 block" color="black">
                  {isDndDisabled
                    ? 'Reordering disabled while loading'
                    : 'Drag any measurement row to re-arrange'}
                </Text>
              )}
            </div>

            <div className="flex-1 overflow-y-auto pr-2 [&::-webkit-scrollbar]:w-2 [&::-webkit-scrollbar-track]:bg-gray-100 [&::-webkit-scrollbar-track]:rounded [&::-webkit-scrollbar-thumb]:bg-gray-400 [&::-webkit-scrollbar-thumb]:rounded [&::-webkit-scrollbar-thumb]:hover:bg-gray-500">
              {isLoading ? (
                <div className="flex justify-center items-center h-[200px]">
                  <Loader />
                </div>
              ) : params.length > 0 ? (
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext items={measurementIds} strategy={verticalListSortingStrategy}>
                    <div
                      className={`space-y-2 ${
                        isDndDisabled ? 'pointer-events-none opacity-60' : ''
                      }`}
                    >
                      {params.map((param) => (
                        <SortableMeasurementItem
                          key={param.id}
                          param={param}
                          onEditName={handleEditName}
                          onEditIcon={handleEditIcon}
                          onDelete={handleDelete}
                          disabled={isDndDisabled}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              ) : (
                <div className="flex flex-col items-center justify-center h-[200px] p-5">
                  <Text color="gray" size="small" className="font-inter">
                    No measurements configured. Add your first measurement below.
                  </Text>
                </div>
              )}
            </div>

            <div className="border-t border-gray-300 pt-4 pb-1">
              <Button
                label="+ Add Measurement"
                size="medium"
                bgColor="primary"
                onClick={() => addMeasurementModalRef.current?.show()}
                className="w-full"
                disabled={!selectedPieceId}
              />
            </div>
          </div>

          <AddMeasurementModal
            ref={addMeasurementModalRef}
            atomicOutfitId={selectedPieceId}
            existingParamIds={existingParamIds}
            onMeasurementAdded={() => {
              void fetchMeasurements();

              if (onMeasurementsUpdated) {
                onMeasurementsUpdated();
              }
            }}
          />

          <EditMeasurementNameModal
            ref={editNameModalRef}
            measurementParam={selectedParamForEdit}
            boutiqueMeasurementsId={measurementsData?.id ?? null}
            categoryName={currentPiece?.name}
            onSaved={() => {
              void fetchMeasurements();

              if (onMeasurementsUpdated) {
                onMeasurementsUpdated();
              }
            }}
          />

          <MeasurementIconSelectorModal
            ref={iconSelectorModalRef}
            onIconSelect={handleIconSelect}
            selectedIconId={selectedParamForIcon?.id}
          />
        </Modal>
      </>
    );
  }
);

MeasurementsModal.displayName = 'MeasurementsModal';

export default MeasurementsModal;
